import { useCallback, useEffect, useRef, useState } from "react";
import {
  arquivarProfessor,
  listarProfessores,
  salvarProfessor,
  sincronizarProfessores,
  type NivelDerivado,
  type OrigemDoNivel,
  type ProfessorDaCentral,
} from "../lib/professoresDaCentral";
import { CAMPO, CampoDeBusca, Recado } from "./ui";
import { useRolarAoEscolher } from "../lib/rolarPara";
import Carregando from "./Carregando";

// Admin › Docentes — o corpo docente, lido e escrito na CENTRAL DE PROFESSORES.
//
// ---------------------------------------------------------------------------
// De onde vem cada coisa desta tela (21/09/2026)
// ---------------------------------------------------------------------------
// Victor decidiu que a Central de Professores (o outro projeto Supabase da
// casa) é a FONTE DA VERDADE do cadastro de professor, e que toda edição feita
// aqui escreve LÁ. Então esta tela não fala com `pos_docentes`: ela fala com a
// Edge Function `professores-da-central`, a porta única entre os dois projetos,
// e cada linha da lista é a JUNÇÃO de dois lugares:
//
//   · quem a pessoa É — nome, contato, cidade — vem da Central;
//   · quantas disciplinas ela tem, em que cursos e se está PROJETADA no hub
//     vêm daqui (`pos_docentes`, que virou espelho com o MESMO id, 000275).
//
// E a TITULAÇÃO (especialista, mestre, doutor) voltou a ser um campo em
// 22/09, a pedido de Victor: *"tem de ser um campo dedicado, editável nas duas
// portas"*, porque é o que sai impresso no certificado. O select grava
// `professores.titulacao` na Central — a mesma coluna que a edição rápida de lá
// edita —, e ela MANDA.
//
// A derivação das formações não morreu: ela é a reserva de quem ainda não tem
// titulação (o cadastro que chega pelo formulário público nasce assim), e a
// SEGUNDA OPINIÃO que esta tela mostra quando as duas discordam. Por isso cada
// linha diz de onde o nível veio — "definido pela equipe", "das formações" ou
// "padrão" —: as três levam a condutas diferentes, e só a última pede que
// alguém olhe.
//
// ---------------------------------------------------------------------------
// Desativar, nunca excluir — e agora com um motivo a mais
// ---------------------------------------------------------------------------
// Apagar na Central leva 17 tabelas em CASCADE (histórico, avaliações,
// participações). Aqui o botão arquiva, que é reversível. E desativar NÃO tira
// o nome dele das disciplinas que ele já tem — reduzir é ato explícito
// (princípio 1). A tela repete isso com letra.
//
// ---------------------------------------------------------------------------
// Zero regra aqui
// ---------------------------------------------------------------------------
// Nome repetido, forma do e-mail, dígitos do telefone: quem valida é a porta
// (e a projeção no hub, como segunda camada), e a tela mostra a frase que
// volta. Repetir em JavaScript criaria a segunda definição.

type Formulario = {
  id: string | null;
  nome: string;
  email: string;
  telefone: string;
  /** "" é o estado NULO da Central — "a casa ainda não disse". */
  titulacao: "" | NivelDerivado;
};

const VAZIO: Formulario = { id: null, nome: "", email: "", telefone: "", titulacao: "" };

/**
 * As três titulações, com o texto que vai no certificado ao lado do nome.
 * A lista é a MESMA da constraint da Central e da `pos_docente_projetar` —
 * três cópias que só convivem porque as três recusam o que não está nelas;
 * a que divergir vira erro de check, não dado torto.
 */
const TITULACOES: ReadonlyArray<{ valor: NivelDerivado; texto: string }> = [
  { valor: "especialista", texto: "Especialista" },
  { valor: "mestre", texto: "Mestre" },
  { valor: "doutor", texto: "Doutor" },
];

/** O que a linha diz sobre a procedência do nível impresso. */
const ORIGEM_TEXTO: Record<OrigemDoNivel, string> = {
  casa: "definido pela equipe",
  formacoes: "das formações da Central",
  padrao: "padrão — ninguém definiu",
};

/**
 * A máscara do telefone é APRESENTAÇÃO — só os dígitos viajam. Doutrina da casa,
 * nascida de um campo em reais que gravava NULO por mandar o texto mascarado.
 */
function mascararTelefone(bruto: string): string {
  const d = bruto.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export default function AdminPosDocentes() {
  const [lista, setLista] = useState<ProfessorDaCentral[] | null>(null);
  const [total, setTotal] = useState(0);
  const [semProjecao, setSemProjecao] = useState(0);
  const [busca, setBusca] = useState("");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [form, setForm] = useState<Formulario | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [erroDeCarga, setErroDeCarga] = useState<string | null>(null);
  // O passo de 25 das telas de admin. São 82 na Central hoje.
  const [mostrando, setMostrando] = useState(25);

  // O formulário mora ACIMA da lista; quem clica em "Editar" no fim precisa ver
  // a tela responder (Victor, 16/09 — o modo de falha em que o clique "não
  // pegou"). `useRolarAoEscolher` desconta o Topo e não rola o que já se vê.
  const caixaDoForm = useRef<HTMLElement | null>(null);
  const formAberto = form ? (form.id ?? "novo") : null;
  useRolarAoEscolher(caixaDoForm, formAberto);
  useEffect(() => {
    if (formAberto === null) return;
    const t = window.setTimeout(() => {
      caixaDoForm.current?.querySelector<HTMLInputElement>('[data-campo="nome"]')?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(t);
  }, [formAberto]);

  const carregar = useCallback(async () => {
    const r = await listarProfessores(busca.trim() || undefined);
    if (!r.ok) {
      setLista([]);
      // A frase é da porta: "a Central de Professores não está configurada",
      // "só a equipe". Ela sabe o que faltou; a tela não adivinha.
      setErroDeCarga(r.detalhe ? `${r.erro} — ${r.detalhe}` : r.erro);
      return;
    }
    setErroDeCarga(null);
    setLista(r.dados.professores);
    setTotal(r.dados.total);
    setSemProjecao(r.dados.sem_projecao);
  }, [busca]);

  useEffect(() => {
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar]);

  useEffect(() => { setMostrando(25); }, [busca, mostrarInativos]);

  async function salvar() {
    if (!form) return;
    setSalvando(true);
    setRecado(null);
    const r = await salvarProfessor(
      { nome: form.nome, email: form.email.trim() || null, whatsapp: form.telefone.replace(/\D/g, "") || null,
        // "" volta a ser NULO na Central: é como se desfaz a escolha sem
        // inventar um quarto valor, e o nível volta a sair das formações.
        titulacao: form.titulacao || null },
      form.id ?? undefined,
    );
    setSalvando(false);
    if (!r.ok) {
      setRecado({ tipo: "erro", texto: r.detalhe ? `${r.erro} ${r.detalhe}` : r.erro });
      return;
    }
    const { professor, projecao } = r.dados;
    // A confirmação diz o ESTADO NOVO — e quando a edição alcançou disciplinas,
    // diz QUANTAS (o padrão da 000224). E se a segunda metade (o espelho no
    // hub) falhou, DIZ: a Central já está certa, e "Sincronizar" refaz o resto.
    const alcance = form.id && (projecao.disciplinas ?? 0) > 0
      ? ` O nome foi atualizado em ${projecao.disciplinas} disciplina(s).` : "";
    const espelho = projecao.ok ? "" : ` A Central gravou, mas o hub não acompanhou (${projecao.detalhe ?? projecao.motivo}) — use "Sincronizar".`;
    setRecado({ tipo: projecao.ok ? "ok" : "erro",
      texto: `${professor.nome} ${form.id ? "atualizado" : "cadastrado"} na Central.${alcance}${espelho}` });
    setForm(null);
    await carregar();
  }

  async function alternarAtivo(p: ProfessorDaCentral) {
    setRecado(null);
    const r = await arquivarProfessor(p.id, !p.arquivado);
    if (!r.ok) { setRecado({ tipo: "erro", texto: r.detalhe ? `${r.erro} ${r.detalhe}` : r.erro }); return; }
    setRecado({ tipo: "ok", texto: p.arquivado
      ? `${p.nome} está ativo de novo.`
      : `${p.nome} foi desativado e não aparece mais para escolher. As ${p.disciplinas} disciplina(s) dele continuam com o nome dele.` });
    await carregar();
  }

  async function sincronizar() {
    setSincronizando(true);
    setRecado(null);
    const r = await sincronizarProfessores();
    setSincronizando(false);
    if (!r.ok) { setRecado({ tipo: "erro", texto: r.detalhe ? `${r.erro} ${r.detalhe}` : r.erro }); return; }
    const { total: t, criados, atualizados, recusados } = r.dados;
    setRecado({ tipo: recusados.length ? "erro" : "ok",
      texto: `${t} professores da Central projetados no hub: ${criados} novos, ${atualizados} atualizados` +
        (recusados.length ? `, ${recusados.length} recusados (${recusados.map((x) => `${x.nome}: ${x.detalhe ?? x.motivo}`).join("; ")})` : "") + "." });
    await carregar();
  }

  if (lista === null) return <Carregando />;

  const visiveis = lista.filter((p) => mostrarInativos || !p.arquivado);
  const inativos = lista.filter((p) => p.arquivado).length;

  return (
    <div className="flex flex-col gap-5">
      {/* ── A barra ──────────────────────────────────────────────────────── */}
      <div className="surface rise p-5 sm:p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-64 flex-1">
            <CampoDeBusca valor={busca} aoMudar={setBusca} rotulo="Buscar docente"
              placeholder="Nome ou e-mail" aoLimpar={busca ? () => setBusca("") : undefined} />
          </div>
          <label className="flex min-h-12 items-center gap-2 text-sm text-cinza">
            <input type="checkbox" checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)} className="size-4 accent-acento" />
            Mostrar desativados
            {inativos > 0 && <span className="text-xs text-cinza">({inativos})</span>}
          </label>
          <button type="button" data-novo-docente
            onClick={() => { setRecado(null); setForm({ ...VAZIO }); }}
            className="pill-cta min-h-12 px-5">
            + Novo docente
          </button>
        </div>

        <p className="mt-4 text-sm text-cinza">
          {total} docente(s) na Central de Professores
          {busca.trim() && ` · ${lista.length} nesta busca`}
        </p>

        {/* Professor sem PROJEÇÃO não pode ser escolhido numa disciplina: o
            hub não tem a linha dele. Dizer isso — e oferecer o conserto — é o
            oposto de esconder um cadastro que existe. */}
        {semProjecao > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl bg-alerta-fundo p-4">
            <p className="flex-1 text-sm text-alerta-tinta" data-sem-projecao={semProjecao}>
              {semProjecao} professor(es) da Central ainda não existem aqui no hub, e por isso não
              aparecem para escolher numa disciplina.
            </p>
            <button type="button" data-sincronizar onClick={() => void sincronizar()} disabled={sincronizando}
              className="min-h-10 rounded-full border border-alerta-tinta/40 px-4 text-sm font-semibold text-alerta-tinta transition hover:bg-alerta-tinta/10 disabled:opacity-50">
              {sincronizando ? "Sincronizando…" : "Sincronizar com a Central"}
            </button>
          </div>
        )}
      </div>

      {erroDeCarga && <Recado tipo="erro" texto={erroDeCarga} />}
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {/* ── O formulário ─────────────────────────────────────────────────── */}
      {form && (
        <section ref={caixaDoForm} className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-form-docente">
          <h3 id="titulo-form-docente" className="font-display text-lg font-bold text-titulo">
            {form.id ? "Editar docente" : "Novo docente"}
          </h3>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Nome</span>
              <input data-campo="nome" className={CAMPO} value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Como aparece no certificado do aluno" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Telefone / WhatsApp</span>
              <input data-campo="telefone" className={CAMPO} value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: mascararTelefone(e.target.value) })}
                placeholder="(00) 00000-0000" inputMode="tel" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">E-mail</span>
              <input data-campo="email" className={CAMPO} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="professor@exemplo.com" inputMode="email" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Titulação</span>
              <select data-campo="titulacao" className={CAMPO} value={form.titulacao}
                onChange={(e) => setForm({ ...form, titulacao: e.target.value as Formulario["titulacao"] })}>
                {/* O vazio NÃO é "nenhuma": é "a casa não disse", e nesse caso
                    o nível sai das formações da Central. Escrever
                    'Especialista' por padrão afirmaria o que ninguém conferiu. */}
                <option value="">— não definida (usa as formações) —</option>
                {TITULACOES.map((t) => (
                  <option key={t.valor} value={t.valor}>{t.texto}</option>
                ))}
              </select>
            </label>
          </div>

          {/* A procedência do que está no select, para a edição não ser um tiro
              no escuro: o valor pode ter vindo das formações, e gravar aqui o
              transforma em decisão da equipe. */}
          {form.id && (() => {
            const p = lista?.find((x) => x.id === form.id);
            if (!p) return null;
            return (
              <p className="mt-3 text-sm text-cinza" data-procedencia-do-nivel={p.nivel_origem}>
                Hoje sai <strong>{p.nivel_texto}</strong> no certificado ({ORIGEM_TEXTO[p.nivel_origem]}).
                {p.nivel_origem !== "casa" && ` As formações da Central indicam ${p.nivel_derivado_texto}.`}
                {p.titulacao_diverge && (
                  <span className="text-alerta-tinta">
                    {" "}As formações indicam <strong>{p.nivel_derivado_texto}</strong> — quem manda é o campo,
                    e alguém precisa dizer qual dos dois está certo.
                  </span>
                )}
              </p>
            );
          })()}

          <p className="mt-4 text-sm text-cinza">
            Isto é gravado na <strong>Central de Professores</strong>, que é o cadastro oficial — a
            mesma titulação aparece na edição rápida de lá. Ela sai impressa no certificado do aluno,
            ao lado do nome.
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" data-salvar-docente onClick={() => void salvar()}
              disabled={salvando || !form.nome.trim()} className="pill-cta min-h-12 px-6 disabled:opacity-50">
              {salvando ? "Salvando…" : form.id ? "Salvar" : "Cadastrar"}
            </button>
            <button type="button" onClick={() => setForm(null)}
              className="min-h-12 rounded-full border border-borda px-6 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta">
              Cancelar
            </button>
          </div>
        </section>
      )}

      {/* ── A lista ──────────────────────────────────────────────────────── */}
      <section className="surface rise overflow-hidden">
        {visiveis.length === 0 ? (
          <p className="p-6 text-sm text-cinza">
            {busca.trim() ? `Nenhum docente encontrado para "${busca.trim()}".` : "Nenhum docente na Central ainda."}
          </p>
        ) : (
          <ul className="divide-y divide-borda">
            {visiveis.slice(0, mostrando).map((p) => (
              <li key={p.id} data-docente={p.id}
                className={`flex flex-wrap items-start gap-4 p-5 ${p.arquivado ? "bg-chip/40" : ""}`}>
                <div className="min-w-56 flex-1">
                  <p className="font-semibold text-titulo">
                    {p.nome}
                    {p.arquivado && (
                      <span className="ml-2 rounded-full bg-chip px-2 py-0.5 text-xs font-normal text-chip-tinta">desativado</span>
                    )}
                    {!p.projetado && (
                      <span className="ml-2 rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-normal text-alerta-tinta" title="Existe na Central, mas ainda não no hub — não pode ser escolhido numa disciplina">
                        sem projeção
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-sm text-cinza">
                    <span data-nivel={p.nivel} data-nivel-origem={p.nivel_origem}>{p.nivel_texto}</span>
                    {/* A procedência fica ao lado do valor: "Especialista
                        porque a equipe decidiu" e "Especialista porque ninguém
                        preencheu" são fatos diferentes, e só o segundo pede que
                        alguém olhe. */}
                    {p.nivel_origem !== "casa" && (
                      <span className="ml-1 text-cinza" title={p.nivel_origem === "padrao"
                        ? "Ninguém definiu a titulação e não há formação registrada na Central"
                        : "Sai das formações registradas na Central"}>
                        ({ORIGEM_TEXTO[p.nivel_origem]})
                      </span>
                    )}
                    {p.titulacao_diverge && (
                      <span className="ml-1 text-alerta-tinta"
                        title={`A equipe definiu ${p.nivel_texto}; as formações da Central indicam ${p.nivel_derivado_texto}`}>
                        · formações dizem {p.nivel_derivado_texto}
                      </span>
                    )}
                    {p.projecao_defasada && (
                      <span className="ml-1 text-alerta-tinta" title="A titulação mudou e o hub ainda mostra a antiga">· projeção defasada</span>
                    )}
                    {p.disciplinas > 0 && ` · ${p.disciplinas} disciplina(s)`}
                    {p.cursos.length > 1 && ` em ${p.cursos.length} cursos`}
                  </p>
                  {(p.email || p.whatsapp) && (
                    <p className="mt-1 text-sm text-cinza">
                      {[p.email, p.whatsapp && mascararTelefone(p.whatsapp)].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button type="button" data-editar={p.id}
                    onClick={() => { setRecado(null); setForm({ id: p.id, nome: p.nome, email: p.email ?? "",
                      telefone: p.whatsapp ? mascararTelefone(p.whatsapp) : "",
                      // O select parte do CAMPO, não do nível efetivo: abrir a
                      // edição de quem não tem titulação e já mostrar "Mestre"
                      // faria salvar qualquer outra coisa gravar uma afirmação
                      // que ninguém fez.
                      titulacao: p.titulacao ?? "" }); }}
                    className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta">
                    Editar
                  </button>
                  <button type="button" data-alternar={p.id} onClick={() => void alternarAtivo(p)}
                    className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta">
                    {p.arquivado ? "Reativar" : "Desativar"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {visiveis.length > mostrando && (
          <div className="border-t border-borda p-4 text-center">
            <button type="button" data-mostrar-mais onClick={() => setMostrando((n) => n + 25)}
              className="min-h-10 rounded-full border border-borda px-5 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta">
              Mostrar mais ({visiveis.length - mostrando} restantes)
            </button>
          </div>
        )}
      </section>

      <p className="text-sm text-cinza">
        Docente não se apaga: as disciplinas dele apontam para este cadastro, e as notas dos alunos
        apontam para as disciplinas. Desativar tira da lista de escolha e mantém tudo o que já foi feito.
        O cadastro completo — currículo, formações, contrato — mora na Central de Professores.
      </p>
    </div>
  );
}
